import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { child, get, push, ref, set } from 'firebase/database';
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import { database, storage } from '../services/firebase';

type Species = 'Dog' | 'Cat' | 'Other';
type Gender = 'Male' | 'Female' | 'Unknown';

export type Pet = {
  id: string;
  name: string;
  nickname?: string;
  species: Species;
  gender?: Gender;
  breed?: string;
  birthDate?: string;
  adoptionDate?: string;
  notes?: string;
  photoUri?: string;
  photoStoragePath?: string;
  microchipSerial?: string | null;
  microchipCompanyId?: string | null;
};

export type PetNote = {
  id: string;
  text: string;
  createdAt: number;
  category?: string;
  value?: string;
  unit?: string;
  type?: string;
  incidentType?: string;
  triggers?: string[];
  context?: string;
  size?: string;
  amount?: string;
  attachments?: PetNoteAttachment[];
};

export type PetNoteAttachment = {
  url: string;
  storagePath?: string;
  fileName?: string;
};

export type PetMedication = {
  id: string;
  petId: string;
  name: string;
  dosage?: string;
  unit?: string;
  unitSize?: string;
  form?: string;
  frequency?: string;
  prescribedOn?: string;
  duration?: string;
  createdAt: number;
};

export type PetAllergy = {
  id: string;
  petId: string;
  name: string;
  reaction?: string;
  notes?: string;
  createdAt: number;
};

export type PetVaccination = {
  id: string;
  petId: string;
  name: string;
  administeredOn?: number;
  notes?: string;
  createdAt: number;
};

export type PetFood = {
  id: string;
  petId: string;
  brand: string;
  kind?: string;
  amount?: string;
  unit?: string;
  notes?: string;
  frequency?: string;
  createdAt: number;
};

export type PetCondition = {
  id: string;
  petId: string;
  name: string;
  category?: string;
  active: boolean;
  diagnosedAt?: number;
  tags?: string[];
  notes?: string;
  createdAt: number;
};

export type PetExternalNote = {
  id: string;
  petId: string;
  content: string;
  createdAt: number;
  context?: string;
  authorUserId?: string;
  authorName?: string;
  attachments?: PetNoteAttachment[];
};

type FirebasePetRecord = {
  name?: string;
  nickname?: string;
  species?: string;
  gender?: string;
  breed?: string;
  birthDate?: string;
  adoptionDate?: string;
  notes?: string;
  photoUri?: string;
  photoStoragePath?: string;
  microchipSerial?: string | null;
  microchipCompanyId?: string | null;
};

type FirebasePetNoteRecord = {
  text?: string;
  createdAt?: number;
  category?: string;
  value?: string;
  unit?: string;
  type?: string;
  incidentType?: string;
  triggers?: string[];
  context?: string;
  size?: string;
  amount?: string;
  attachments?: unknown;
};

type FirebasePetAttachmentRecord = {
  url?: string;
  storagePath?: string;
  fileName?: string;
  name?: string;
};

type FirebasePetMedicationRecord = {
  name?: string;
  dosage?: string;
  unit?: string;
  unitSize?: string;
  form?: string;
  frequency?: string;
  prescribedOn?: string;
  duration?: string;
  createdAt?: number;
};

type FirebasePetAllergyRecord = {
  name?: string;
  reaction?: string;
  notes?: string;
  createdAt?: number;
};

type FirebasePetVaccinationRecord = {
  name?: string;
  administeredOn?: number;
  notes?: string;
  createdAt?: number;
};

type FirebasePetConditionRecord = {
  name?: string;
  category?: string;
  active?: boolean;
  diagnosedAt?: number;
  tags?: unknown;
  notes?: string;
  createdAt?: number;
};

type FirebasePetFoodRecord = {
  brand?: string;
  kind?: string;
  amount?: string;
  unit?: string;
  notes?: string;
  frequency?: string;
  createdAt?: number;
};

type FirebasePetTokenRecord = {
  petId?: string;
  createdAt?: number;
  noteIds?: unknown;
  expiresAt?: number | null;
};

type FirebasePetExternalNoteRecord = {
  content?: string;
  createdAt?: number;
  context?: string;
  authorUserId?: string;
  authorName?: string;
  attachments?: unknown;
};

type SummaryStatus = 'idle' | 'loading' | 'ready' | 'error';

type SummaryState = {
  status: SummaryStatus;
  pet: Pet | null;
  notes: PetNote[];
  externalNotes: PetExternalNote[];
  medications: PetMedication[];
  foods: PetFood[];
  allergies: PetAllergy[];
  vaccinations: PetVaccination[];
  conditions: PetCondition[];
  errorMessage?: string;
};

type SummaryContextValue = SummaryState & {
  loadSummary: (token: string) => Promise<void>;
  reset: () => void;
  addExternalNote: (
    petId: string,
    payload: {
      content: string;
      context?: string;
      authorName?: string;
      authorUserId?: string;
      attachments?: File[];
    }
  ) => Promise<void>;
};

const PetSummaryContext = createContext<SummaryContextValue | undefined>(undefined);

function normalizeSpecies(value: unknown): Species {
  if (value === 'Dog' || value === 'Cat') {
    return value;
  }
  return 'Other';
}

function normalizeGender(value: unknown): Gender | undefined {
  if (value === 'Male' || value === 'Female' || value === 'Unknown') {
    return value;
  }
  return undefined;
}

function stripUndefined<T extends Record<string, unknown>>(entry: T): T {
  return Object.fromEntries(
    Object.entries(entry).filter(([, value]) => value !== undefined)
  ) as T;
}

function inferFileExtension(file: File): string {
  if (typeof file.name === 'string') {
    const match = /\.([A-Za-z0-9]+)(?:\?.*)?$/.exec(file.name);
    if (match?.[1]) {
      return match[1].toLowerCase();
    }
  }

  if (typeof file.type === 'string' && file.type.includes('/')) {
    const [, subtype] = file.type.split('/');
    if (subtype && subtype.trim().length > 0) {
      return subtype.trim().toLowerCase();
    }
  }

  return 'bin';
}

function normalizePet(id: string, record: FirebasePetRecord | null | undefined): Pet {
  if (!record) {
    throw new Error('Missing pet record.');
  }

  const {
    name = 'Unnamed Pet',
    nickname,
    species,
    gender,
    breed,
    birthDate,
    adoptionDate,
    notes,
    photoUri,
    photoStoragePath,
    microchipSerial,
    microchipCompanyId,
  } = record;

  const normalizedMicrochipSerial =
    microchipSerial === null
      ? null
      : typeof microchipSerial === 'string' && microchipSerial.trim().length > 0
        ? microchipSerial.trim()
        : undefined;
  const normalizedMicrochipCompanyId =
    microchipCompanyId === null
      ? null
      : typeof microchipCompanyId === 'string' && microchipCompanyId.trim().length > 0
        ? microchipCompanyId.trim()
        : undefined;

  return {
    id,
    name,
    nickname,
    species: normalizeSpecies(species),
    gender: normalizeGender(gender),
    breed,
    birthDate,
    adoptionDate,
    notes,
    photoUri,
    photoStoragePath,
    microchipSerial: normalizedMicrochipSerial,
    microchipCompanyId: normalizedMicrochipCompanyId,
  };
}

function normalizeNotes(records: Record<string, FirebasePetNoteRecord> | null | undefined): PetNote[] {
  if (!records) {
    return [];
  }

  return Object.entries(records)
    .map(([id, entry]) => ({
      id,
      text: entry.text ?? '',
      createdAt: typeof entry.createdAt === 'number' ? entry.createdAt : 0,
      category: entry.category,
      value: entry.value,
      unit: entry.unit,
      type: entry.type,
      incidentType: entry.incidentType,
      triggers: entry.triggers,
      context: entry.context,
      size: entry.size,
      amount: entry.amount,
      attachments: normalizeAttachments(entry.attachments),
    }))
    .sort((a, b) => b.createdAt - a.createdAt);
}

function normalizeMedications(petId: string, records: Record<string, FirebasePetMedicationRecord> | null | undefined): PetMedication[] {
  if (!records) {
    return [];
  }

  return Object.entries(records)
    .map(([id, entry]) => ({
      id,
      petId,
      name: entry.name ?? '',
      dosage: entry.dosage,
      unit: entry.unit,
      unitSize: entry.unitSize,
      form: entry.form,
      frequency: entry.frequency,
      prescribedOn: entry.prescribedOn,
      duration: entry.duration,
      createdAt: typeof entry.createdAt === 'number' ? entry.createdAt : 0,
    }))
    .sort((a, b) => b.createdAt - a.createdAt);
}

function normalizeAllergies(petId: string, records: Record<string, FirebasePetAllergyRecord> | null | undefined): PetAllergy[] {
  if (!records) {
    return [];
  }

  return Object.entries(records)
    .map(([id, entry]) => ({
      id,
      petId,
      name: entry.name ?? '',
      reaction: entry.reaction,
      notes: entry.notes,
      createdAt: typeof entry.createdAt === 'number' ? entry.createdAt : 0,
    }))
    .sort((a, b) => b.createdAt - a.createdAt);
}

function normalizeVaccinations(petId: string, records: Record<string, FirebasePetVaccinationRecord> | null | undefined): PetVaccination[] {
  if (!records) {
    return [];
  }

  return Object.entries(records)
    .map(([id, entry]) => ({
      id,
      petId,
      name: entry.name ?? '',
      administeredOn: typeof entry.administeredOn === 'number' ? entry.administeredOn : undefined,
      notes: entry.notes,
      createdAt: typeof entry.createdAt === 'number' ? entry.createdAt : 0,
    }))
    .sort((a, b) => b.createdAt - a.createdAt);
}

function normalizeFoods(
  petId: string,
  records: Record<string, FirebasePetFoodRecord> | null | undefined
): PetFood[] {
  if (!records) {
    return [];
  }

  return Object.entries(records)
    .map(([id, entry]) => ({
      id,
      petId,
      brand: entry.brand ?? '',
      kind: entry.kind,
      amount: entry.amount,
      unit: entry.unit,
      notes: entry.notes,
      frequency: entry.frequency,
      createdAt: typeof entry.createdAt === 'number' ? entry.createdAt : 0,
    }))
    .sort((a, b) => b.createdAt - a.createdAt);
}

function normalizeConditionTags(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const tags = value
    .map(item => (typeof item === 'string' ? item.trim() : ''))
    .filter(tag => tag.length > 0);

  return tags.length > 0 ? tags : undefined;
}

function normalizeAttachments(value: unknown): PetNoteAttachment[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const entries = value
    .map(item => {
      if (!item || typeof item !== 'object') {
        return undefined;
      }
      const { url, storagePath, fileName, name } = item as FirebasePetAttachmentRecord;
      if (typeof url !== 'string' || url.trim().length === 0) {
        return undefined;
      }
      const trimmedUrl = url.trim();
      const trimmedPath =
        typeof storagePath === 'string' && storagePath.trim().length > 0 ? storagePath.trim() : undefined;
      const candidateName =
        typeof fileName === 'string' && fileName.trim().length > 0
          ? fileName.trim()
          : typeof name === 'string' && name.trim().length > 0
            ? name.trim()
            : undefined;
      const inferredName =
        candidateName ??
        (trimmedPath ? trimmedPath.split('/').pop() : undefined);
      const attachment: PetNoteAttachment = trimmedPath
        ? { url: trimmedUrl, storagePath: trimmedPath }
        : { url: trimmedUrl };
      if (inferredName) {
        attachment.fileName = inferredName;
      }
      return attachment;
    })
    .filter((entry): entry is PetNoteAttachment => Boolean(entry));

  return entries.length ? entries : undefined;
}

function parseTokenNoteIds(value: unknown): Set<string> | undefined {
  if (!value) {
    return undefined;
  }

  const addIds = (ids: string[]): Set<string> | undefined => {
    const clean = ids
      .map(id => (typeof id === 'string' ? id.trim() : ''))
      .filter(id => id.length > 0);
    return clean.length > 0 ? new Set(clean) : undefined;
  };

  if (Array.isArray(value)) {
    return addIds(value as string[]);
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, flag]) => {
        if (typeof flag === 'boolean') {
          return flag;
        }
        if (typeof flag === 'number') {
          return flag !== 0;
        }
        if (typeof flag === 'string') {
          const normalized = flag.trim().toLowerCase();
          return normalized !== '' && normalized !== 'false' && normalized !== '0';
        }
        return false;
      })
      .map(([key]) => key);
    return addIds(entries);
  }

  return undefined;
}

function normalizeConditions(petId: string, records: Record<string, FirebasePetConditionRecord> | null | undefined): PetCondition[] {
  if (!records) {
    return [];
  }

  return Object.entries(records)
    .map(([id, entry]) => ({
      id,
      petId,
      name: entry.name ?? '',
      category: typeof entry.category === 'string' ? entry.category : undefined,
      active: entry.active !== false,
      diagnosedAt: typeof entry.diagnosedAt === 'number' ? entry.diagnosedAt : undefined,
      tags: normalizeConditionTags(entry.tags),
      notes: entry.notes,
      createdAt: typeof entry.createdAt === 'number' ? entry.createdAt : 0,
    }))
    .sort((a, b) => b.createdAt - a.createdAt);
}

function normalizeExternalNotes(
  petId: string,
  records: Record<string, FirebasePetExternalNoteRecord> | null | undefined,
): PetExternalNote[] {
  if (!records) {
    return [];
  }

  return Object.entries(records)
    .map(([id, entry]) => ({
      id,
      petId,
      content: entry.content ?? '',
      createdAt: typeof entry.createdAt === 'number' ? entry.createdAt : 0,
      context: entry.context,
      authorUserId: entry.authorUserId,
      authorName: entry.authorName,
      attachments: normalizeAttachments(entry.attachments),
    }))
    .sort((a, b) => b.createdAt - a.createdAt);
}

const INITIAL_STATE: SummaryState = {
  status: 'idle',
  pet: null,
  notes: [],
  externalNotes: [],
  medications: [],
  foods: [],
  allergies: [],
  vaccinations: [],
  conditions: [],
  errorMessage: undefined,
};

export const PetSummaryProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [state, setState] = useState<SummaryState>(INITIAL_STATE);
  const activeTokenRef = useRef<string | null>(null);

  const loadSummary = useCallback(async (token: string) => {
    if (!token) {
      return;
    }

    activeTokenRef.current = token;
    setState(prev => ({
      ...prev,
      status: 'loading',
      errorMessage: undefined,
    }));

    try {
      const rootRef = ref(database);

      const tokenSnapshot = await get(child(rootRef, `petTokens/${token}`));
      const tokenRecord = tokenSnapshot.exists() ? (tokenSnapshot.val() as FirebasePetTokenRecord) : null;
      const noteFilter = parseTokenNoteIds(tokenRecord?.noteIds);
      const expiresAt = typeof tokenRecord?.expiresAt === 'number' ? tokenRecord.expiresAt : null;

      if (expiresAt && Date.now() > expiresAt) {
        throw new Error('This shared link has expired. Please request a new one from the pet owner.');
      }

      let petId = tokenRecord?.petId ?? '';

      if (!petId) {
        const legacySnapshot = await get(child(rootRef, `petShareTokens/${token}`));
        petId = legacySnapshot.exists() ? (legacySnapshot.val()?.petId as string | undefined) ?? '' : '';
      }

      if (!petId) {
        petId = token;
      }

      const petSnapshot = await get(child(rootRef, `pets/${petId}`));
      if (!petSnapshot.exists()) {
        throw new Error('Pet not found or no access for this token.');
      }

      const normalizedPet = normalizePet(petId, petSnapshot.val() as FirebasePetRecord);
      const [
        notesSnapshot,
        externalNotesSnapshot,
        medicationsSnapshot,
        foodsSnapshot,
        allergiesSnapshot,
        vaccinationsSnapshot,
        conditionsSnapshot,
      ] = await Promise.all([
        get(child(rootRef, `petNotes/${petId}`)),
        get(child(rootRef, `petExternalNotes/${petId}`)),
        get(child(rootRef, `petMedications/${petId}`)),
        get(child(rootRef, `petFoods/${petId}`)),
        get(child(rootRef, `petAllergies/${petId}`)),
        get(child(rootRef, `petVaccinations/${petId}`)),
        get(child(rootRef, `petConditions/${petId}`)),
      ]);

      const normalizedNotes = normalizeNotes(notesSnapshot.val() as Record<string, FirebasePetNoteRecord> | null);
      const filteredNotes = noteFilter
        ? normalizedNotes.filter(note => noteFilter.has(note.id))
        : normalizedNotes;
      const normalizedExternalNotes = normalizeExternalNotes(
        petId,
        externalNotesSnapshot.val() as Record<string, FirebasePetExternalNoteRecord> | null,
      );

      const normalizedMedications = normalizeMedications(
        petId,
        medicationsSnapshot.val() as Record<string, FirebasePetMedicationRecord> | null
      );
      const normalizedFoods = normalizeFoods(
        petId,
        foodsSnapshot.val() as Record<string, FirebasePetFoodRecord> | null
      );
      const normalizedAllergies = normalizeAllergies(
        petId,
        allergiesSnapshot.val() as Record<string, FirebasePetAllergyRecord> | null
      );
      const normalizedVaccinations = normalizeVaccinations(
        petId,
        vaccinationsSnapshot.val() as Record<string, FirebasePetVaccinationRecord> | null
      );
      const normalizedConditions = normalizeConditions(
        petId,
        conditionsSnapshot.val() as Record<string, FirebasePetConditionRecord> | null
      );

      if (activeTokenRef.current !== token) {
        return;
      }

      setState({
        status: 'ready',
        pet: normalizedPet,
        notes: filteredNotes,
        externalNotes: normalizedExternalNotes,
        medications: normalizedMedications,
        foods: normalizedFoods,
        allergies: normalizedAllergies,
        vaccinations: normalizedVaccinations,
        conditions: normalizedConditions,
        errorMessage: undefined,
      });
    } catch (error) {
      if (activeTokenRef.current !== token) {
        return;
      }

      const message = error instanceof Error ? error.message : 'Failed to load pet summary.';
      setState({
        status: 'error',
        pet: null,
        notes: [],
        externalNotes: [],
        medications: [],
        foods: [],
        allergies: [],
        vaccinations: [],
        conditions: [],
        errorMessage: message,
      });
    }
  }, []);

  const addExternalNote = useCallback(
    async (
      petId: string,
      payload: {
        content: string;
        context?: string;
        authorName?: string;
        authorUserId?: string;
        attachments?: File[];
      }
    ) => {
      const trimmedContent = payload.content.trim();
      const attachmentsToUpload = Array.isArray(payload.attachments) ? payload.attachments : [];

      if (!trimmedContent && attachmentsToUpload.length === 0) {
        throw new Error('Cannot save an empty note.');
      }

      const trimmedAuthorName = payload.authorName?.trim() ?? '';
      const trimmedAuthorUserId = payload.authorUserId?.trim();
      const trimmedContext = payload.context?.trim() ?? '';
      if (attachmentsToUpload.length > 0 && !trimmedAuthorUserId) {
        throw new Error('Cannot attach files without an authenticated user.');
      }

      const notesRef = ref(database, `petExternalNotes/${petId}`);
      const newNoteRef = push(notesRef);
      const noteId = newNoteRef.key;

      if (!noteId) {
        throw new Error('Failed to generate an identifier for the new note.');
      }

      const attachments: PetNoteAttachment[] = [];

      for (let index = 0; index < attachmentsToUpload.length; index += 1) {
        const file = attachmentsToUpload[index]!;
        if (!(file instanceof File)) {
          continue;
        }

        const extension = inferFileExtension(file);
        const storagePath = `petExternalNotes/${trimmedAuthorUserId}/${petId}/${noteId}-${index}.${extension}`;
        const objectRef = storageRef(storage, storagePath);
        const metadata = file.type ? { contentType: file.type } : undefined;

        await uploadBytes(objectRef, file, metadata);
        const url = await getDownloadURL(objectRef);
        const trimmedFileName =
          typeof file.name === 'string' && file.name.trim().length > 0 ? file.name.trim() : undefined;
        attachments.push(stripUndefined({ url, storagePath, fileName: trimmedFileName }));
      }

      const createdAt = Date.now();
      const record = stripUndefined({
        content: trimmedContent || undefined,
        createdAt,
        context: trimmedContext || undefined,
        authorUserId: trimmedAuthorUserId ?? undefined,
        authorName: trimmedAuthorName || undefined,
        attachments: attachments.length ? attachments : undefined,
      });

      await set(newNoteRef, record);

      void (async () => {
        try {
          const petSnapshot = await get(child(ref(database), `pets/${petId}`));
          const petRecord = petSnapshot.exists() ? (petSnapshot.val() as { ownerId?: string; name?: string }) : null;
          const ownerUserId =
            typeof petRecord?.ownerId === 'string' && petRecord.ownerId.trim().length > 0
              ? petRecord.ownerId.trim()
              : null;
          if (!ownerUserId) {
            return;
          }

          const petName =
            typeof petRecord?.name === 'string' && petRecord.name.trim().length > 0
              ? petRecord.name.trim()
              : 'your pet';

          const displayAuthorName = trimmedAuthorName || 'Your veterinary team';
          const notificationsRef = ref(database, `userNotifications/${ownerUserId}`);
          const newNotificationRef = push(notificationsRef);
          await set(
            newNotificationRef,
            stripUndefined({
              subject: `New note for ${petName}`,
              message: `${displayAuthorName} left a note for ${petName}. Tap to view the note.`,
              createdAt,
              read: false,
              action: {
                type: 'navigate',
                screen: 'ExternalNotesDetail',
                params: {
                  petId,
                  noteId,
                },
                label: 'View note',
              },
            }),
          );
        } catch (notificationError) {
          console.error('Failed to create notification for new external note', notificationError);
        }
      })();

      const externalNote: PetExternalNote = {
        id: noteId,
        petId,
        content: trimmedContent,
        createdAt,
        context: record.context as string | undefined,
        authorUserId: record.authorUserId as string | undefined,
        authorName: record.authorName as string | undefined,
        attachments: attachments.length ? attachments : undefined,
      };

      setState(prev => {
        if (!prev.pet || prev.pet.id !== petId) {
          return prev;
        }

        const nextExternalNotes = [externalNote, ...prev.externalNotes].sort(
          (a, b) => b.createdAt - a.createdAt,
        );

        return {
          ...prev,
          externalNotes: nextExternalNotes,
        };
      });
    },
    [],
  );

  const reset = useCallback(() => {
    activeTokenRef.current = null;
    setState({ ...INITIAL_STATE });
  }, []);

  const value = useMemo<SummaryContextValue>(
    () => ({
      ...state,
      loadSummary,
      reset,
      addExternalNote,
    }),
    [state, loadSummary, reset, addExternalNote]
  );

  return <PetSummaryContext.Provider value={value}>{children}</PetSummaryContext.Provider>;
};

export function usePetSummary(): SummaryContextValue {
  const context = useContext(PetSummaryContext);
  if (!context) {
    throw new Error('usePetSummary must be used within a PetSummaryProvider.');
  }
  return context;
}
